import React from 'react';
import { ColorPaletteProvider } from '@apps/guest';
import { Flex, Box, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { GuestSiteEventDesign } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { ProductListViewSkeleton } from '@apps/registry/guest/routes/GuestRegistry/components/ProductList/ProductListViewSkeleton';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { Shell } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/components/Shell';
import { StyledMainSection, styles } from '@apps/registry/guest/routes/GuestRegistry/GuestRegistry.styles';
import {
  StyledRoot,
  StyledTitle,
  CoupleWrapper,
  NoteAndPhotoRow,
  StyledTextBox,
  styles as coupleHeaderStyles,
} from '@apps/registry/guest/routes/GuestRegistry/components/CoupleHeader/CoupleHeader.styles';
import { DesignLayoutType } from '@graphql/generated-base';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { getEnabledPages } from '@apps/registry/guest/utils';

type RegistryShellProps = Readonly<{
  eventHandle: string;
  eventDesign?: GuestSiteEventDesign;
  displayName?: string;
  pages?: ReadonlyArray<Pick<EventPageFragment, 'id' | 'disabled' | 'type'>>;
  hasPhoto?: boolean;
  layout?: DesignLayoutType;
  children?: React.ReactNode;
}>;

/**
 * Header skeleton matching BrannanHeader structure.
 * Shows event title and NavBar placeholder if multiple pages exist.
 */
const HeaderSkeleton: React.FC<{
  displayName?: string;
  showNavBarPlaceholder: boolean;
}> = ({ displayName, showNavBarPlaceholder }) => {
  return (
    <>
      <StyledRoot
        position="relative"
        minHeight={[291, 200, null, 'initial']}
        paddingTop={8}
      >
        <StyledTitle __css={coupleHeaderStyles.title}>
          {displayName ? (
            <TextV2
              typographyVariant="display6"
              fontWeight={400}
              marginBottom={[7, null, 56]}
            >
              {displayName}
            </TextV2>
          ) : (
            <SkeletonText
              rows={1}
              skeletonHeight={pxToRem(48)}
              width={pxToRem(280)}
            />
          )}
        </StyledTitle>
      </StyledRoot>

      {/* NavBar placeholder */}
      {showNavBarPlaceholder && (
        <Flex
          justifyContent="center"
          alignItems="center"
          paddingX={['10px', null, '0px']}
          width="100%"
          marginTop={-56}
        >
          <Flex
            gap={6}
            paddingY={4}
          >
            <SkeletonText
              rows={1}
              skeletonHeight={pxToRem(16)}
              width={pxToRem(60)}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={pxToRem(16)}
              width={pxToRem(70)}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={pxToRem(16)}
              width={pxToRem(55)}
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};

/**
 * Couple section skeleton matching CoupleWrapper structure.
 * Shows date, page note, and photo placeholders.
 */
const CoupleSectionSkeleton: React.FC<{
  hasPhoto: boolean;
}> = ({ hasPhoto }) => {
  return (
    <CoupleWrapper margin={{ md: '0 auto' }}>
      <NoteAndPhotoRow>
        <StyledTextBox fullWidth={!hasPhoto}>
          <Box>
            {/* Date skeleton */}
            <Box
              width="100%"
              marginBottom={5}
            >
              <SkeletonText
                rows={1}
                skeletonHeight={pxToRem(20)}
                width={pxToRem(140)}
              />
            </Box>
            {/* Page note skeleton */}
            <Box>
              <SkeletonText
                rows={3}
                skeletonHeight={pxToRem(18)}
                width="100%"
              />
            </Box>
          </Box>
        </StyledTextBox>

        {/* Photo placeholder */}
        {hasPhoto && (
          <Box
            width={['100%', null, pxToRem(410)]}
            maxWidth={pxToRem(410)}
          >
            <SkeletonThumbnail
              width="100%"
              paddingTop="66.6%"
              borderRadius={2}
            />
          </Box>
        )}
      </NoteAndPhotoRow>
    </CoupleWrapper>
  );
};

/**
 * Footer skeleton matching RegistryFooter structure.
 */
const FooterSkeleton: React.FC = () => {
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      marginY={120}
      gap={6}
    >
      <SkeletonText
        rows={1}
        skeletonHeight={pxToRem(14)}
        width={pxToRem(40)}
      />
      <SkeletonThumbnail
        width={pxToRem(24)}
        height={pxToRem(24)}
        borderRadius="50%"
      />
      <SkeletonText
        rows={1}
        skeletonHeight={pxToRem(14)}
        width={pxToRem(50)}
      />
    </Flex>
  );
};

/**
 * Registry shell skeleton that matches the actual page structure.
 * Uses real Shell component and layout styles to ensure visual consistency.
 */
export const RegistryShell: React.FC<RegistryShellProps> = ({ eventHandle, eventDesign, displayName, pages, hasPhoto = false, layout, children }) => {
  const { showNavigationBar } = getEnabledPages(pages);

  return (
    <ColorPaletteProvider eventDesign={eventDesign}>
      <Shell
        eventDesign={eventDesign}
        eventHandle={eventHandle}
        noTranslucency
        isAlohaGuestPage={layout === DesignLayoutType.aloha}
        showTopPadding
        bypassStyleApplicatorLoading
      >
        {/* Header section matching BrannanHeader */}
        <HeaderSkeleton
          displayName={displayName}
          showNavBarPlaceholder={showNavigationBar}
        />

        {/* Couple section matching CoupleWrapper */}
        <CoupleSectionSkeleton hasPhoto={hasPhoto} />

        {/* Main content section */}
        <Flex __css={styles.wrapperNewMvp}>
          <StyledMainSection __css={styles.mainSection}>
            {children || <ProductListViewSkeleton />}
            <FooterSkeleton />
          </StyledMainSection>
        </Flex>
      </Shell>
    </ColorPaletteProvider>
  );
};
